import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700 border-slate-300" },
  DRAFT: { label: "Draft", color: "bg-slate-100 text-slate-700 border-slate-300" },
  SENT: { label: "Sent", color: "bg-blue-100 text-blue-700 border-blue-300" },
  ACCEPTED: { label: "Accepted", color: "bg-green-100 text-green-700 border-green-300" },
  REJECTED: { label: "Rejected", color: "bg-red-100 text-red-700 border-red-300" },
  INVOICED: { label: "Invoiced", color: "bg-purple-100 text-purple-700 border-purple-300" },
  CANCELLED: { label: "Cancelled", color: "bg-orange-100 text-orange-700 border-orange-300" },
};

export const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase rounded-sm border ${cfg.color}`} data-testid="quote-status-badge">{cfg.label}</span>;
};

export const formatCurrency = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);
export const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

export const emptyLine = () => ({ part_number_raw: "", part_name: "", description: "", qty: 1, uom: "Nos", brand: "", unit_price: 0, gst_rate: 18, hsn_code: "", notes: "" });

export function LineItemsEditor({ lines, updateLine, addLine, removeLine, calcLineTotal }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-bold uppercase text-slate-600">Line Items</Label>
        <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn" className="rounded-sm text-xs uppercase font-bold">
          <Plus size={14} className="mr-1" /> Add Line
        </Button>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-x-auto" data-testid="line-items-editor">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase w-28">Part #</th>
              <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase">Name *</th>
              <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase w-16">Qty</th>
              <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase w-16">UOM</th>
              <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase w-24">Price</th>
              <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase w-16">GST%</th>
              <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase w-24">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id || `line-${i}`} className="border-b border-slate-100">
                <td className="px-2 py-1"><Input value={l.part_number_raw} onChange={(e) => updateLine(i, "part_number_raw", e.target.value)} className="rounded-sm border-slate-300 text-xs h-8" data-testid={`line-part-${i}`} /></td>
                <td className="px-2 py-1"><Input value={l.part_name} onChange={(e) => updateLine(i, "part_name", e.target.value)} className="rounded-sm border-slate-300 text-xs h-8" data-testid={`line-name-${i}`} /></td>
                <td className="px-2 py-1"><Input type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", parseInt(e.target.value) || 0)} className="rounded-sm border-slate-300 text-xs h-8 text-right" data-testid={`line-qty-${i}`} /></td>
                <td className="px-2 py-1"><Input value={l.uom} onChange={(e) => updateLine(i, "uom", e.target.value)} className="rounded-sm border-slate-300 text-xs h-8" data-testid={`line-uom-${i}`} /></td>
                <td className="px-2 py-1"><Input type="number" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", parseFloat(e.target.value) || 0)} className="rounded-sm border-slate-300 text-xs h-8 text-right" data-testid={`line-price-${i}`} /></td>
                <td className="px-2 py-1"><Input type="number" value={l.gst_rate} onChange={(e) => updateLine(i, "gst_rate", parseFloat(e.target.value) || 0)} className="rounded-sm border-slate-300 text-xs h-8 text-right" data-testid={`line-gst-${i}`} /></td>
                <td className="px-2 py-1 text-right text-xs font-semibold text-slate-700">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(calcLineTotal(l))}</td>
                <td className="px-1 py-1"><Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700" disabled={lines.length === 1}><X size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-1">{lines.length} line{lines.length !== 1 ? "s" : ""}</p>
    </div>
  );
}
